import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useClubMembership } from '../hooks/useClubMembership'
import { usePageTitle } from '../hooks/usePageTitle'
import { useCourseBook, useCourseCard } from '../hooks/useCourseBook'
import {
  AreaSectionHead,
  ClubLinkButton,
  ClubPage,
  Container,
  CourseCard,
  CourseScorecard,
  MemberAvatar,
  PageHead,
  SignInPrompt,
} from '../components/golf'

/**
 * The Grounds (route map §4 `/clubhouse/grounds`) — the courses, the party,
 * and how a round reads.
 *
 * THE COURSES ARE REAL. This page used to render three invented ones out of
 * `lib/courses.ts` — St. Regrets, The Quicknine, Exit Links — with an invented
 * nine-hole card beneath them, while the club's fifteen actual New York
 * municipal courses sat in `Fireside::Golf::Courses` and in the database. That
 * file is deleted; the book is `GET /golf/courses` with no coordinates, and the
 * card below is `GET /golf/courses/:key`, fetched when a course is chosen.
 *
 * Both endpoints are public, so there is no gate here and none is rendered — a
 * visitor deciding whether to apply can see where the club plays.
 *
 * Three things this page deliberately does NOT print:
 *   * A course RECORD. Nothing publishes one, and "Unclaimed" beside fifteen
 *     real municipal courses would be a claim about fifteen real places.
 *   * The member's own em-dashed score row on the card. It stood in for web
 *     play, which is not coming: the club is read-only on the web and the
 *     Caddie app is where a round is kept.
 *   * Anything the server publishes as null. Par and yardage are ALL OR
 *     NOTHING on the wire, and a half-carded course says so.
 */
export function TheGroundsScreen() {
  usePageTitle('The Grounds')
  const { isAuthenticated, user } = useAuth()
  const { membership } = useClubMembership()
  const { courses, isLoading, error } = useCourseBook()
  const [selected, setSelected] = useState<string | null>(null)

  // Nothing is chosen until a member chooses, and then the club reads exactly
  // that one card. The first course in the book stands in as the opening page.
  const openTo = selected ?? courses[0]?.key ?? null
  const { card, isLoading: cardLoading, notCarded, error: cardError } = useCourseCard(openTo)

  const name = user?.display_name?.trim() || user?.email?.split('@')[0]
  const drawn = courses.filter((course) => course.mapped).length

  return (
    <ClubPage chrome="clubhouse">
      <PageHead
        eyebrow="The Grounds"
        title="Always in"
        emphasis="season."
        lede="The courses the club plays, as they are actually carded — par, stroke index and yardage, hole by hole. Choose a course to read its card."
        stats={[
          // A dash while the book is still opening: "0 courses on the books"
          // is a false statement, briefly, and this page exists to stop those.
          { value: isLoading ? '—' : String(courses.length), label: 'Courses on the books' },
          { value: isLoading ? '—' : String(drawn), label: 'Drawn for the map' },
          { value: '0', label: 'Tee sheets kept' },
        ]}
      />

      {/* ── The course book ── */}
      <section className="pb-16">
        <Container>
          <AreaSectionHead eyebrow="The course book" title="Where the club plays." />

          <div className="rounded-club-lg border border-ink/12 bg-parchment p-7.5">
            {isLoading && <p className="text-[13.5px] text-ink-soft">Opening the course book…</p>}
            {error && (
              <p role="alert" className="text-[13.5px] text-flag">
                {error}
              </p>
            )}

            {!isLoading && !error && (
              <ul className="grid gap-4.5 lg:grid-cols-3">
                {courses.map((course) => (
                  <CourseCard
                    key={course.key}
                    course={course}
                    selected={course.key === openTo}
                    onSelect={setSelected}
                  />
                ))}
              </ul>
            )}

            {/* ── Your party ── */}
            <div className="mt-6.5 flex flex-wrap items-center gap-6 border-t border-brass/30 pt-6">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-brass-deep">
                Your party
              </span>
              {isAuthenticated ? (
                <div className="flex items-center gap-2.5">
                  <MemberAvatar name={name} size="sm" />
                  <div>
                    <p className="text-[13.5px] font-semibold text-ink">{name}</p>
                    {/* Was "No number struck yet", asserted about every signed-in
                        member. Read from the standing now, and omitted entirely
                        when there is none to read. */}
                    {membership?.member_number && (
                      <p className="mt-px text-[11px] text-ink-soft">{membership.member_number}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-ink-soft">
                  Sign in and the club will put your name down.
                </p>
              )}
            </div>

            {/* ── Where a round is kept ── */}
            <div className="mt-6.5 flex flex-wrap items-center justify-between gap-6 border-t border-brass/30 pt-6">
              <p className="flex max-w-[58ch] items-baseline gap-2.5 text-[13px] text-ink-soft">
                <span className="shrink-0 rounded-club-sm border border-brass/55 bg-gilt/14 px-2 py-[3px] text-[9.5px] font-bold uppercase tracking-[0.16em] text-brass-deep">
                  Preview
                </span>
                Rounds are kept on the course, in the Caddie app. The club&rsquo;s book is here.
              </p>
              <ClubLinkButton to="/clubhouse" variant="ghost">
                Back to the clubhouse
              </ClubLinkButton>
            </div>
          </div>
        </Container>
      </section>

      {/* ── The scorecard ── */}
      <section className="pb-16">
        <Container>
          <AreaSectionHead eyebrow="The Scorecard" title="How a round reads." />

          <div className="rounded-club-lg border border-ink/12 bg-parchment px-6.5 pb-1.5 pt-6.5">
            {cardLoading && <p className="pb-5 text-[13.5px] text-ink-soft">Reading the card…</p>}

            {cardError && (
              <p role="alert" className="pb-5 text-[13.5px] text-flag">
                {cardError}
              </p>
            )}

            {notCarded && (
              <p className="pb-5 text-[13.5px] text-ink-soft">
                The club has not carded this course yet. Its par and yardages will appear here once
                somebody has walked it with a pencil.
              </p>
            )}

            {card && card.holes.length > 0 && (
              <>
                <CourseScorecard holes={card.holes} caption={card.name} />
                <p className="px-3.5 pb-4 pt-3 font-display text-[12.5px] italic text-ink-soft">
                  {card.name}, {card.city}. Stroke index is the order the strokes come off — first
                  is the hardest.
                </p>
              </>
            )}
          </div>

          {!isAuthenticated && <SignInPrompt what="your own card" className="mt-8" />}
        </Container>
      </section>
    </ClubPage>
  )
}
